import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Mail, Phone, MapPin, LogOut, Inbox, Store, Wallet, Briefcase, Download } from "lucide-react";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const Admin = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("contatti");
  const [contatti, setContatti] = useState([]);
  const [affiliazioni, setAffiliazioni] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("commpaq_token");
    if (!token) {
      navigate("/login");
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/admin/contatti`, { headers }),
      axios.get(`${API}/admin/affiliazioni`, { headers }),
      axios.get(`${API}/admin/catalogo-leads`, { headers }),
    ])
      .then(([c, a, l]) => {
        setContatti(c.data);
        setAffiliazioni(a.data);
        setLeads(l.data);
        setLoading(false);
      })
      .catch(() => {
        localStorage.removeItem("commpaq_token");
        navigate("/login");
      });
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("commpaq_token");
    navigate("/login");
  };

  const items = tab === "contatti" ? contatti : tab === "affiliazioni" ? affiliazioni : leads;

  return (
    <div data-testid="admin-page" className="min-h-screen pt-28 pb-24">
      <Seo title="Area Riservata | COMMPAQ" description="Pannello amministrativo COMMPAQ." robots="noindex,nofollow" />
      <div className="mx-auto max-w-6xl px-6 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-sm uppercase tracking-[0.3em] text-moss">Area Riservata</p>
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-none text-forest sm:text-5xl">
              Richieste ricevute
            </h1>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 rounded-full border border-forest/20 px-6 py-3 text-xs uppercase tracking-[0.15em] text-forest transition-[background-color,border-color] duration-300 hover:border-moss hover:bg-moss/10"
          >
            <LogOut size={14} />
            Esci
          </button>
        </div>

        <div className="mt-12 flex gap-3">
          <button
            onClick={() => setTab("contatti")}
            data-testid="admin-tab-contatti"
            className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm uppercase tracking-[0.12em] transition-[background-color,color] duration-300 ${
              tab === "contatti" ? "bg-forest text-bone" : "border border-forest/20 text-forest hover:bg-moss/10"
            }`}
          >
            <Inbox size={15} />
            Contatti ({contatti.length})
          </button>
          <button
            onClick={() => setTab("affiliazioni")}
            data-testid="admin-tab-affiliazioni"
            className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm uppercase tracking-[0.12em] transition-[background-color,color] duration-300 ${
              tab === "affiliazioni" ? "bg-forest text-bone" : "border border-forest/20 text-forest hover:bg-moss/10"
            }`}
          >
            <Store size={15} />
            Candidature Risorice ({affiliazioni.length})
          </button>
          <button
            onClick={() => setTab("leads")}
            data-testid="admin-tab-leads"
            className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm uppercase tracking-[0.12em] transition-[background-color,color] duration-300 ${
              tab === "leads" ? "bg-forest text-bone" : "border border-forest/20 text-forest hover:bg-moss/10"
            }`}
          >
            <Download size={15} />
            Download catalogo ({leads.length})
          </button>
        </div>

        {loading ? (
          <p data-testid="admin-loading" className="mt-16 text-forest/60">
            Caricamento...
          </p>
        ) : items.length === 0 ? (
          <p data-testid="admin-empty" className="mt-16 text-forest/60">
            Nessuna richiesta presente.
          </p>
        ) : (
          <div data-testid={`admin-list-${tab}`} className="mt-10 space-y-4">
            {items.map((item) => (
              <div
                key={item.id}
                data-testid={`admin-item-${item.id}`}
                className="border border-forest/10 bg-white p-6 transition-shadow duration-300 hover:shadow-lg hover:shadow-moss/10 sm:p-8"
              >
                {tab === "leads" ? (
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <a
                      href={`mailto:${item.email}`}
                      className="inline-flex items-center gap-2 font-display text-xl font-bold text-forest hover:text-moss"
                    >
                      <Mail size={18} className="text-moss" />
                      {item.email}
                    </a>
                    <span className="text-xs uppercase tracking-[0.15em] text-forest/50">
                      {formatDate(item.created_at)}
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-display text-xl font-bold text-forest">{item.nome}</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.15em] text-forest/50">
                      {formatDate(item.created_at)}
                    </p>
                  </div>
                  {tab === "contatti" ? (
                    <span className="rounded-full bg-moss/10 px-4 py-1.5 text-xs uppercase tracking-[0.12em] text-moss">
                      {item.interesse}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-moss/10 px-4 py-1.5 text-xs uppercase tracking-[0.12em] text-moss">
                      <MapPin size={12} />
                      {item.citta}
                    </span>
                  )}
                </div>

                <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2 text-sm text-forest/70">
                  <a href={`mailto:${item.email}`} className="inline-flex items-center gap-2 hover:text-moss">
                    <Mail size={14} className="text-moss" />
                    {item.email}
                  </a>
                  {item.telefono && (
                    <span className="inline-flex items-center gap-2">
                      <Phone size={14} className="text-moss" />
                      {item.telefono}
                    </span>
                  )}
                  {tab === "affiliazioni" && item.budget && (
                    <span className="inline-flex items-center gap-2">
                      <Wallet size={14} className="text-moss" />
                      {item.budget}
                    </span>
                  )}
                  {tab === "affiliazioni" && item.esperienza && (
                    <span className="inline-flex items-center gap-2">
                      <Briefcase size={14} className="text-moss" />
                      {item.esperienza}
                    </span>
                  )}
                </div>

                    {item.messaggio && (
                      <p className="mt-5 border-l-2 border-moss pl-4 text-sm leading-relaxed text-forest/75">
                        {item.messaggio}
                      </p>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
